import { IS_TESTNET } from '~/config.js';
import axios from 'axios';
import { getAccountStateBoc } from '~/api/toncenterV3.js';

/* eslint camelcase: "off", func-names: "off" */

const verifiedSourceByAddress = new Map();

const verifiedSourceSessionKey = function (address) {
    return `tonscan:verifier:v3:${IS_TESTNET ? 'testnet:' : ''}${address}`;
};

const readVerifiedSourceSession = function (address) {
    try {
        const raw = sessionStorage.getItem(verifiedSourceSessionKey(address));
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
};

const writeVerifiedSourceSession = function (address, data) {
    try {
        sessionStorage.setItem(verifiedSourceSessionKey(address), JSON.stringify(data));
    } catch (_) {
        // Quota or private mode — in-memory cache still works for this visit.
    }
};

const VERIFIER_SOURCE_ENDPOINT = 'https://verifier.ton.org/api/v1/verification/source';

// verifier.ton.org keeps sources by code hash, the same for every network.
// The answer is brought to the shape the contract tab has always read:
const fetchVerifiedSource = async function (address) {
    const account = await getAccountStateBoc(address);

    // Lazy: the catalog module knows every contract file, which the main bundle should not.
    const { contractCodeHash } = await import(/* webpackChunkName: "abi-catalog" */ '~/abi/catalog.js');
    const codeHash = contractCodeHash(account?.code_hash, account?.code_boc);

    // 404 is a regular answer: nobody has verified this code.
    const { data } = codeHash
        ? await axios.get(VERIFIER_SOURCE_ENDPOINT, {
            params: { code_hash: codeHash },
            validateStatus: status => status === 200 || status === 404,
        })
        : {};

    if (!data?.verified || !data.bundle) {
        return { codeHash, files: [], verificationDate: null, compiler: null, compilerSettings: null };
    }

    const { files, entrypoint, compiler, verified_at: verifiedAt } = data.bundle;

    return {
        codeHash,
        files: files.map(file => ({
            name: file.path,
            content: file.content,
            isEntrypoint: file.path === entrypoint || file.path.endsWith(`/${entrypoint}`),
        })),
        verificationDate: new Date(verifiedAt * 1000).toISOString(),
        compiler: compiler.language,
        compilerSettings: { version: compiler.version },
    };
};

/**
 * @param  {String} address
 * @return {Promise<Object>}
 */
export const getVerifiedSourceByAddress = function (address) {
    if (verifiedSourceByAddress.has(address)) {
        return verifiedSourceByAddress.get(address);
    }

    const cached = readVerifiedSourceSession(address);
    if (cached) {
        const cachedPromise = Promise.resolve(cached);
        verifiedSourceByAddress.set(address, cachedPromise);
        return cachedPromise;
    }

    const request = fetchVerifiedSource(address)
        .then((data) => {
            writeVerifiedSourceSession(address, data);
            return data;
        })
        .catch((error) => {
            verifiedSourceByAddress.delete(address);
            throw error;
        });

    verifiedSourceByAddress.set(address, request);
    return request;
};
